import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Building2, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fetchServiceLegalDocuments, type ServiceLegalDocumentsDescriptor } from "@/api/auth";
import { ApiError } from "@/api/client";
import {
  normalizePostalCodeInput,
  POSTAL_CODE_PATTERN,
  usePostalCodeAddressAutofill,
  type PostalCodeLookupState,
} from "./postalCodeAddressLookup";

export function OrganizationCreateForm({ onCancel }: { onCancel?: () => void }) {
  const { t } = useTranslation();
  const { createCustomer } = useAuth();
  const navigate = useNavigate();
  const [organizationName, setOrganizationName] = useState("");
  const [organizationPhoneNumber, setOrganizationPhoneNumber] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [postalCodeLookupEnabled, setPostalCodeLookupEnabled] = useState(false);
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const inFlight = useRef(false);
  const [termsLoadVersion, setTermsLoadVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [terms, setTerms] = useState<ServiceLegalDocumentsDescriptor | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const postalCodeLookupState = usePostalCodeAddressAutofill({
    postalCode,
    addressLine1,
    enabled: postalCodeLookupEnabled,
    setAddressLine1,
  });

  useEffect(() => {
    let active = true;
    void fetchServiceLegalDocuments()
      .then((value) => {
        if (active) setTerms(value);
      })
      .catch(() => {
        if (active) setError("最新の約款を取得できませんでした。再読み込みしてください。");
      });
    return () => {
      active = false;
    };
  }, [termsLoadVersion]);

  const handleCreateOrganization = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (inFlight.current) return;
    const customerName = organizationName.trim();
    const phoneNumber = organizationPhoneNumber.trim();
    const zip = postalCode.trim();
    const address = addressLine1.trim();
    const building = addressLine2.trim();
    if (!customerName) {
      setError(t("organizationRequired.createNameRequired"));
      return;
    }
    if (!/^[0-9]{10,11}$/.test(phoneNumber)) {
      setError(t("organizationRequired.phoneInvalid"));
      return;
    }
    if (!POSTAL_CODE_PATTERN.test(zip)) {
      setError(t("organizationRequired.postalCodeInvalid"));
      return;
    }
    if (!address) {
      setError(t("organizationRequired.addressRequired"));
      return;
    }
    if (!terms || !termsAccepted) {
      setError("利用条件への同意が必要です。");
      return;
    }
    inFlight.current = true;
    setIsCreating(true);
    setError(null);
    try {
      await createCustomer({
        customerName,
        organizationPhoneNumber: phoneNumber,
        postalCode: zip,
        addressLine1: address,
        addressLine2: building || null,
        legalDocumentsAccepted: true,
        serviceTermsVersion: terms.serviceTerms.version,
        cookiePolicyVersion: terms.cookiePolicy.version,
        termsAndPoliciesVersion: terms.termsAndPolicies.version,
      });
      navigate("/console/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "legal_documents_version_not_current") {
        setTerms(null);
        setTermsAccepted(false);
        setTermsLoadVersion((version) => version + 1);
        setError("約款が更新されました。更新後の内容を確認して、もう一度同意してください。");
      } else {
        setError(err instanceof Error ? err.message : t("organizationRequired.createFailed"));
      }
    } finally {
      inFlight.current = false;
      setIsCreating(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Building2 className="h-5 w-5" />
            </span>
            <div>
              <CardTitle asChild className="text-lg">
                <h2>{t("organizationRequired.formTitle")}</h2>
              </CardTitle>
              <CardDescription>
                {t("organizationRequired.formDescription")}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form aria-label={t("organizationRequired.formTitle")} className="space-y-3" onSubmit={handleCreateOrganization}>
            <div className="space-y-2">
              <Label htmlFor="organization-name">
                {t("organizationRequired.createNameLabel")}
              </Label>
              <Input
                id="organization-name"
                autoFocus
                value={organizationName}
                onChange={(event) => setOrganizationName(event.target.value)}
                placeholder={t("organizationRequired.createNamePlaceholder")}
                disabled={isCreating}
              />
            </div>
            <fieldset className="space-y-3 rounded-md border bg-muted/20 p-4">
              <legend className="px-1 text-sm font-semibold">サービス利用とお試しプラン</legend>
              <p className="text-sm text-muted-foreground">
                {terms?.releasePhase === "beta" ? "ベータ版の提供期間中は無料です。正式公開後も30日間お試しいただけます。" : "組織を登録すると、その時点から30日間のお試しが始まります。"}自動で課金されることはありません。
              </p>
              <TermsCheckbox checked={termsAccepted} onChange={setTermsAccepted} disabled={isCreating || !terms}>
                <a className="font-medium text-primary underline underline-offset-4" href={terms?.termsAndPolicies.url ?? "#"} target="_blank" rel="noreferrer">約款・ポリシー一覧</a>を確認し、
                <a className="font-medium text-primary underline underline-offset-4" href={terms?.serviceTerms.url ?? "#"} target="_blank" rel="noreferrer">利用約款</a>と
                <a className="font-medium text-primary underline underline-offset-4" href={terms?.cookiePolicy.url ?? "#"} target="_blank" rel="noreferrer">Cookieポリシー</a>に同意します。
              </TermsCheckbox>
            </fieldset>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="organization-phone">
                  {t("organizationRequired.phoneLabel")}
                </Label>
                <Input
                  id="organization-phone"
                  value={organizationPhoneNumber}
                  onChange={(event) =>
                    setOrganizationPhoneNumber(
                      event.target.value.replace(/\D/g, "").slice(0, 11),
                    )
                  }
                  placeholder={t("organizationRequired.phonePlaceholder")}
                  inputMode="numeric"
                  maxLength={11}
                  disabled={isCreating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="organization-postal-code">
                  {t("organizationRequired.postalCodeLabel")}
                </Label>
                <Input
                  id="organization-postal-code"
                  value={postalCode}
                  onChange={(event) => {
                    setPostalCode(normalizePostalCodeInput(event.target.value));
                    setPostalCodeLookupEnabled(true);
                  }}
                  placeholder={t("organizationRequired.postalCodePlaceholder")}
                  inputMode="numeric"
                  maxLength={7}
                  disabled={isCreating}
                />
                <PostalCodeLookupMessage state={postalCodeLookupState} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="organization-address-line1">
                {t("organizationRequired.addressLine1Label")}
              </Label>
              <Input
                id="organization-address-line1"
                value={addressLine1}
                onChange={(event) => setAddressLine1(event.target.value)}
                placeholder={t("organizationRequired.addressLine1Placeholder")}
                disabled={isCreating}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="organization-address-line2">
                {t("organizationRequired.addressLine2Label")}
              </Label>
              <Input
                id="organization-address-line2"
                value={addressLine2}
                onChange={(event) => setAddressLine2(event.target.value)}
                placeholder={t("organizationRequired.addressLine2Placeholder")}
                disabled={isCreating}
              />
            </div>
            {error ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                {error}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              {onCancel && (
                <Button type="button" variant="outline" disabled={isCreating} onClick={onCancel}>
                  {t("actions.cancel")}
                </Button>
              )}
              <Button type="submit" disabled={isCreating || !terms}>
                {isCreating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {t("organizationRequired.createSubmit")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <p className="rounded-md border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        {t("organizationRequired.inviteNote")}
      </p>
    </>
  );
}

function TermsCheckbox({ checked, onChange, disabled, children }: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled: boolean;
  children: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 text-sm leading-6">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 rounded border-input accent-primary"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        disabled={disabled}
      />
      <span>{children}</span>
    </label>
  );
}

function PostalCodeLookupMessage({ state }: { state: PostalCodeLookupState }) {
  const { t } = useTranslation();
  if (state === "idle") return null;
  const messageKey = {
    searching: "postalCodeLookup.searching",
    autofilled: "postalCodeLookup.autofilled",
    found: "postalCodeLookup.found",
    not_found: "postalCodeLookup.notFound",
    error: "postalCodeLookup.error",
  }[state];
  return (
    <p
      className={
        state === "not_found" || state === "error"
          ? "text-xs text-destructive"
          : "text-xs text-muted-foreground"
      }
    >
      {t(messageKey)}
    </p>
  );
}
